// import styles
import "./Automezzi.css";

// import pacchetti
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { timestamp } from "../../firebase/config";

// import hooks
import { useCollection } from "../../hooks/useCollection";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useFirestore } from "../../hooks/useFirestore";

export default function Automezzi() {
  const { user } = useAuthContext();
  const { addDocument, updateDocument, deleteDocument, response } =
    useFirestore("automezzi");
  const { documents: settoriDocs } = useCollection("settori");
  const { documents: automezziDocs } = useCollection("automezzi");
  const location = useLocation();
  const navigazione = useNavigate();

  // State per gestire i valori degli input
  const [targa, setTarga] = useState("");
  const [modello, setModello] = useState("");
  const [carburante, setCarburante] = useState("");
  const [autostrade, setAutostrade] = useState("");
  const [scadenzaRevisione, setScadenzaRevisione] = useState("");
  const [scadenzaAssicurazione, setScadenzaAssicurazione] = useState("");
  const [polizzaVeicolo, setPolizzaVeicolo] = useState("");
  const [polizzaConducente, setPolizzaConducente] = useState("");
  const [settore, setSettore] = useState(null);
  const [settoriOptions, setSettoriOptions] = useState([]);
  const [formError, setFormError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [selectedAutomezzo, setSelectedAutomezzo] = useState(null);

  // Preparazione delle opzioni per il select dei settori
  useEffect(() => {
    if (settoriDocs) {
      const options = settoriDocs.map((settore) => {
        return {
          value: settore.id,
          label: settore.nome,
        };
      });
      setSettoriOptions(options);
    }
  }, [settoriDocs]);

  // Preparazione delle opzioni per il select di ricerca automezzi
  const [automezziOptions, setAutomezziOptions] = useState([]);
  useEffect(() => {
    if (automezziDocs) {
      const options = automezziDocs.map((auto) => ({
        value: auto.id,
        label: `${auto.modello} - ${auto.targa}`,
        original: auto,
      }));
      setAutomezziOptions(options);
    }
  }, [automezziDocs]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError(null);

    // Validazione
    if (!settore) {
      setFormError("Seleziona un settore di appartenenza");
      return;
    }

    const autore = {
      displayName: user.displayName,
      photoURL: user.photoURL,
      id: user.uid,
    };

    const automezzoObj = {
      targa,
      modello,
      carburante,
      autostrade,
      scadenzaRevisione,
      scadenzaAssicurazione,
      polizzaVeicolo,
      polizzaConducente,
      settoreId: settore.value,
      settoreNome: settore.label,
      createdAt: timestamp.fromDate(new Date()),
      autore,
    };

    if (isEditing && currentId) {
      // Aggiornamento di un automezzo esistente
      updateDocument(currentId, automezzoObj);
    } else {
      // Creazione di un nuovo automezzo
      addDocument(automezzoObj);
    }
  };

  const resetForm = () => {
    setIsEditing(false);
    setCurrentId(null);
    setTarga("");
    setModello("");
    setCarburante("");
    setAutostrade("");
    setScadenzaRevisione("");
    setScadenzaAssicurazione("");
    setPolizzaVeicolo("");
    setPolizzaConducente("");
    setSettore(null);
    setSelectedAutomezzo(null);
  };

  const handleEdit = useCallback(
    (automezzo) => {
      setIsEditing(true);
      setCurrentId(automezzo.id);
      setTarga(automezzo.targa);
      setModello(automezzo.modello);
      setCarburante(automezzo.carburante);
      setAutostrade(automezzo.autostrade);
      setScadenzaRevisione(automezzo.scadenzaRevisione || "");
      setScadenzaAssicurazione(automezzo.scadenzaAssicurazione || "");
      setPolizzaVeicolo(automezzo.polizzaVeicolo || "");
      setPolizzaConducente(automezzo.polizzaConducente || "");

      // Trova il settore corrispondente nelle opzioni
      const settoreOption = settoriOptions.find(
        (option) => option.value === automezzo.settoreId,
      );
      setSettore(settoreOption);

      // Imposta il select di ricerca automezzo
      setSelectedAutomezzo({
        value: automezzo.id,
        label: `${automezzo.modello} - ${automezzo.targa}`,
        original: automezzo,
      });
    },
    [settoriOptions],
  );

  const handleSelectAutomezzo = (option) => {
    if (option) {
      handleEdit(option.original);
    } else {
      resetForm();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo automezzo?")) {
      await deleteDocument(id);
      resetForm();
    }
  };

  useEffect(() => {
    if (response.success) {
      resetForm();
    }
  }, [response.success]);

  // Carica i dati dell'automezzo se viene passato un ID tramite query parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const automezzoId = queryParams.get("id");

    if (automezzoId && automezziDocs && settoriOptions.length > 0) {
      const automezzo = automezziDocs.find((doc) => doc.id === automezzoId);
      if (automezzo) {
        handleEdit(automezzo);
      }
    }
  }, [location.search, automezziDocs, settoriOptions, handleEdit]);

  return (
    <div>
      <div className="title-container">
        <h2 className="page-title">Gestione Automezzi</h2>
        <button
          onClick={() => navigazione("/automezzi-dashboard")}
          className="btn"
        >
          Dashboard Automezzi
        </button>
      </div>

      <div className="automezzi-container">
        <div className="automezzi-form full-width">
          <div className="automezzi-search-section">
            <h3>Seleziona Automezzo da Modificare</h3>
            <div className="search-controls">
              <Select
                className="search-select"
                onChange={handleSelectAutomezzo}
                options={automezziOptions}
                value={selectedAutomezzo}
                isSearchable={true}
                placeholder="Cerca automezzo per targa o modello..."
                isClearable={true}
              />
              <button
                className="btn btn-secondary"
                onClick={resetForm}
                title="Nuovo Automezzo"
              >
                <span className="material-icons">add</span> Nuovo
              </button>
            </div>
          </div>

          <hr className="divider" />

          <h2 className="page-title">
            {isEditing ? `Modifica: ${modello} (${targa})` : "Nuovo Automezzo"}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <label>
                <span>Targa:</span>
                <input
                  required
                  type="text"
                  onChange={(e) => setTarga(e.target.value)}
                  value={targa}
                />
              </label>
              <label>
                <span>Modello:</span>
                <input
                  required
                  type="text"
                  onChange={(e) => setModello(e.target.value)}
                  value={modello}
                />
              </label>
              <label>
                <span>Carburante:</span>
                <input
                  required
                  type="text"
                  onChange={(e) => setCarburante(e.target.value)}
                  value={carburante}
                />
              </label>
              <label>
                <span>Autostrade:</span>
                <input
                  required
                  type="text"
                  onChange={(e) => setAutostrade(e.target.value)}
                  value={autostrade}
                />
              </label>
              <label>
                <span>Scadenza Revisione:</span>
                <input
                  type="date"
                  onChange={(e) => setScadenzaRevisione(e.target.value)}
                  value={scadenzaRevisione}
                />
              </label>
              <label>
                <span>Scadenza Assicurazione:</span>
                <input
                  type="date"
                  onChange={(e) => setScadenzaAssicurazione(e.target.value)}
                  value={scadenzaAssicurazione}
                />
              </label>
              <label>
                <span>Polizza Veicolo:</span>
                <input
                  type="text"
                  onChange={(e) => setPolizzaVeicolo(e.target.value)}
                  value={polizzaVeicolo}
                />
              </label>
              <label>
                <span>Polizza Conducente:</span>
                <input
                  type="text"
                  onChange={(e) => setPolizzaConducente(e.target.value)}
                  value={polizzaConducente}
                />
              </label>
              <label className="full-width-input">
                <span>Settore di appartenenza:</span>
                <Select
                  onChange={(option) => setSettore(option)}
                  options={settoriOptions}
                  value={settore}
                  isSearchable={true}
                />
              </label>
            </div>

            <div className="form-buttons">
              <button className="btn">
                {isEditing ? "Aggiorna Automezzo" : "Crea Automezzo"}
              </button>
              {isEditing && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Annulla Modifiche
                  </button>
                  <button
                    type="button"
                    className="btn btn-delete"
                    onClick={() => handleDelete(currentId)}
                    style={{
                      marginLeft: "auto",
                      backgroundColor: "#e74c3c",
                      color: "white",
                    }}
                  >
                    Elimina Automezzo
                  </button>
                </>
              )}
            </div>
            {formError && <p className="error">{formError}</p>}
          </form>
        </div>
      </div>
    </div>
  );
}
